import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  ModalFooter,
  Card,
  CardTitle,
  CardBody,
  Collapse,
  Col,
  Row,
  Input,
  Label,
} from "reactstrap";
import { FormattedMessage } from "react-intl";
import { GetLicenseManagementCourses } from "../../actions/superUserQueries";
import { useMutation, useQueryClient, useQuery } from "react-query";
import "@devexpress/dx-react-grid-bootstrap4/dist/dx-react-grid-bootstrap4.css";
import "open-iconic/font/css/open-iconic-bootstrap.css";
import {
  SortingState,
  IntegratedSorting,
  EditingState,
} from "@devexpress/dx-react-grid";
import {
  Grid,
  TableHeaderRow,
  Table,
  TableColumnResizing,
  TableEditColumn,
  TableEditRow,
} from "@devexpress/dx-react-grid-bootstrap4";
import {
  CreateNewScormLicenseCourse,
  UpdateLicenseCourse,
} from "../../actions/superUserMutations";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "react-toastify";
import { injectIntl } from "react-intl";
import moment from "moment";

function LicMgtHandleCoursesModal({ toggle, isOpen, intl }) {
  const [rows, setRows] = useState([]);
  const [newCourse, setNewCourse] = useState({ name: "" });
  const [addNewCourseOpen, setAddNewCourseOpen] = useState(false);
  const [editingRowIds, setEditingRowIds] = useState([]);
  const [rowChanges, setRowChanges] = useState({});

  const licenseCourses = useQuery(
    "FetchLicenseManagementCourses",
    GetLicenseManagementCourses()
  );

  const handleAddNewScormLicenseCourse = useMutation(
    CreateNewScormLicenseCourse
  );

  const handleUpdateLicenseCourse = useMutation(UpdateLicenseCourse);

  const queryClient = useQueryClient();

  const handleToggle = () => {
    setAddNewCourseOpen(false);
    toggle();
  };

  const columns = [
    { name: "name", title: <FormattedMessage id="Name" /> },
    { name: "identifier", title: "Identifierare" },
    { name: "additionalInfo", title: "Övrig info" },
    { name: "created", title: <FormattedMessage id="Created" /> },
  ];

  let sizingExtensions = [
    { columnName: "name", width: 150 },
    { columnName: "identifier", width: 170 },
    { columnName: "additionalInfo", width: 250 },
    { columnName: "created", width: 170 },
  ];
  const getRowId = (row) => row.id;

  useEffect(() => {
    const courses = [];
    if (!licenseCourses.isLoading) {
      licenseCourses.data.forEach((c) => {
        let course = {
          id: c.id,
          name: c.name,
          identifier: c.identifier,
          additionalInfo: c.additionalInfo,
          created: moment(c.created).format("L"),
        };
        courses.push(course);
      });
      setRows(courses);
    }
  }, [licenseCourses.data, licenseCourses.isLoading]);

  const postUpdateCourse = (course, changedRows) => {
    handleUpdateLicenseCourse.mutate(
      {
        id: course.id,
        name: course.name,
        identifier: course.identifier,
        additionalInfo: course.additionalInfo,
      },
      {
        onSuccess: (data) => {
          if (data && data.status > 199 && data.status < 300) {
            toast.success("Kurs uppdaterad");
            if (changedRows !== null) {
              setRows(changedRows);
            }
          } else {
            toast.warn(data.response.data);
          }
        },
        throwOnError: true,
        onError: (err) => {
          toast.warn(err);
        },
      }
    );
  };
  const commitChanges = async ({ changed }) => {
    let changedRows;

    if (changed) {
      changedRows = rows.map((row) =>
        changed[row.id] ? { ...row, ...changed[row.id] } : row
      );
    }

    rows.forEach((r) => {
      if (changed[r.id]) {
        postUpdateCourse({ ...r, ...changed[r.id] }, changedRows);
      }
    });
  };

  const handleAddNewCourse = () => {
    if (newCourse.name.length > 0) {
      handleAddNewScormLicenseCourse.mutate(
        {
          name: newCourse.name,
          identifier: newCourse.identifier,
          additionalInfo: newCourse.additionalInfo,
        },
        {
          onSuccess: (data) => {
            if (data && data.status > 199 && data.status < 300) {
              toast.success(intl.formatMessage({ id: "CourseCreated" }));
              setAddNewCourseOpen(false);
              queryClient.setQueryData(
                ["FetchLicenseManagementCourses"],
                [...licenseCourses.data, data.data.result]
              );
            } else {
              toast.warn(data.response.data);
            }
          },
          throwOnError: true,
          onError: (err) => {
            toast.warn(err);
          },
        }
      );
    }
  };

  return (
    <Modal
      size="xl"
      style={{ maxWidth: "70%", width: "100%" }}
      isOpen={isOpen}
      toggle={handleToggle}
    >
      <ModalHeader>
        <FormattedMessage id="Courses" />
      </ModalHeader>
      <ModalBody>
        <Card>
          <CardBody>
            <CardTitle>
              <FormattedMessage id="Courses" />{" "}
              <Button
                className="ms-3"
                outline
                color="primary"
                onClick={() => setAddNewCourseOpen(!addNewCourseOpen)}
              >
                <FontAwesomeIcon
                  style={{ marginRight: ".5em" }}
                  className={
                    addNewCourseOpen
                      ? "fa-caret--animated collapsed"
                      : "fa-caret--animated"
                  }
                  icon={"caret-up"}
                />
                <FormattedMessage id="AddCourse" />
              </Button>
            </CardTitle>

            <Collapse isOpen={addNewCourseOpen} className="border my-3 p-2">
              <>
                <Row className="mt-3 mb-1">
                  <Col>
                    <Label className="me-2 mb-0" for="newCourseName">
                      <FormattedMessage id="CourseName" />:
                    </Label>
                    <Input
                      name="newCourseName"
                      type="text"
                      onChange={(data) =>
                        setNewCourse({ ...newCourse, name: data.target.value })
                      }
                    />
                  </Col>
                  <Col>
                    <Label className="me-2 mb-0" for="newCourseIdentifier">
                      <FormattedMessage id="IdentifierForCourse" />:
                    </Label>
                    <Input
                      name="newCourseIdentifier"
                      type="text"
                      onChange={(data) =>
                        setNewCourse({
                          ...newCourse,
                          identifier: data.target.value,
                        })
                      }
                    />
                  </Col>
                  <Col>
                    <Label className="me-2 mb-0" for="newCourseAdditionalInfo">
                      <FormattedMessage id="AdditionalInfo" />:
                    </Label>
                    <Input
                      name="newCourseAdditionalInfo"
                      type="text"
                      onChange={(data) =>
                        setNewCourse({
                          ...newCourse,
                          additionalInfo: data.target.value,
                        })
                      }
                    />
                  </Col>
                </Row>
                <Row className="pt-1">
                  <Col>
                    <Button
                      outline
                      className="float-end"
                      color="primary"
                      onClick={() => handleAddNewCourse()}
                    >
                      <FormattedMessage id="AddCourse" />
                    </Button>
                    <Button
                      color="secondary"
                      outline
                      className="me-2 float-end"
                      onClick={() => setAddNewCourseOpen(false)}
                    >
                      <FormattedMessage id="Cancel" />
                    </Button>
                  </Col>
                </Row>
              </>
            </Collapse>
            <Grid rows={rows} columns={columns} getRowId={getRowId}>
              <SortingState />
              <IntegratedSorting />
              <EditingState
                editingRowIds={editingRowIds}
                onEditingRowIdsChange={setEditingRowIds}
                rowChanges={rowChanges}
                onRowChangesChange={setRowChanges}
                onCommitChanges={commitChanges}
              />
              <Table striped={true} />
              <TableColumnResizing defaultColumnWidths={sizingExtensions} />
              <TableHeaderRow showSortingControls />
              <TableEditRow />
              <TableEditColumn
                messages={{
                  editCommand: intl.formatMessage({ id: "Edit" }),
                  commitCommand: intl.formatMessage({ id: "Save" }),
                  cancelCommand: intl.formatMessage({ id: "Cancel" }),
                }}
                showEditCommand
              />
            </Grid>
          </CardBody>
        </Card>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={handleToggle}>
          <FormattedMessage id="Cancel" />
        </Button>
      </ModalFooter>
    </Modal>
  );
}
export default injectIntl(LicMgtHandleCoursesModal);
